/**
 * Zentrale Projekt-Navigations-Konfiguration.
 *
 * Single Source of Truth für:
 *   - label (Text in Menüs)
 *   - title (Seitentitel oben auf der Seite)
 *   - browserTitle Suffix
 *   - route (relativer Pfad nach /app/projekte/:projectId/)
 *   - icon (lucide-react Komponente)
 *   - roles (welche Rollen sehen den Menüpunkt — undefined = alle)
 *
 * Wer einen Menüpunkt umbenennen will, ändert NUR hier — die linke Subnav,
 * die ProjectTopNav-Komponente und die Seitenüberschriften ziehen alle aus
 * dieser Datei.
 */
import {
  LayoutDashboard,
  ClipboardList,
  ListTodo,
  FileText,
  TrendingUp,
  Calculator,
  FolderOpen,
  CalendarDays,
  Settings,
  Map,
} from "lucide-react";

export const PROJECT_PAGES = {
  overview: {
    key: "overview",
    label: "Projektübersicht",
    shortLabel: "Übersicht",
    title: "Projektübersicht",
    route: "dashboard",
    icon: LayoutDashboard,
    testKey: "dashboard",
  },
  daten: {
    key: "daten",
    label: "Projektdaten",
    shortLabel: "Daten",
    title: "Projektdaten",
    route: "daten",
    icon: ClipboardList,
    testKey: "daten",
  },
  tasks: {
    key: "tasks",
    label: "Aufgaben",
    shortLabel: "Aufgaben",
    title: "Projektaufgaben",
    route: "aufgaben",
    icon: ListTodo,
    testKey: "aufgaben",
  },
  dailyReports: {
    key: "dailyReports",
    label: "Bautagesberichte",
    shortLabel: "Berichte",
    title: "Bautagesberichte",
    route: "berichte",
    icon: FileText,
    testKey: "berichte",
  },
  controlling: {
    key: "controlling",
    label: "Controlling",
    shortLabel: "Controlling",
    title: "Projektcontrolling",
    route: "controlling",
    icon: TrendingUp,
    testKey: "controlling",
  },
  calculation: {
    key: "calculation",
    label: "Kalkulation",
    shortLabel: "Kalkulation",
    title: "Projektkalkulation",
    route: "kalkulation",
    icon: Calculator,
    testKey: "kalkulation",
    roles: ["admin"],
  },
  documents: {
    key: "documents",
    label: "Dokumente",
    shortLabel: "Dokumente",
    title: "Projektdokumente",
    route: "dokumente",
    icon: FolderOpen,
    testKey: "dokumente",
  },
  plans: {
    key: "plans",
    label: "Pläne",
    shortLabel: "Pläne",
    title: "Projektpläne",
    route: "plaene",
    icon: Map,
    testKey: "plaene",
  },
  planning: {
    key: "planning",
    label: "Einsatzplanung",
    shortLabel: "Planung",
    title: "Projekteinsatzplanung",
    // Globale Einsatzplanung — kein projekt-spezifischer Pfad
    absoluteRoute: "/app/einsatzplanung",
    icon: CalendarDays,
    testKey: "einsatzplanung",
  },
  settings: {
    key: "settings",
    label: "Einstellungen",
    shortLabel: "Settings",
    title: "Projekteinstellungen",
    route: "einstellungen",
    icon: Settings,
    testKey: "einstellungen",
    roles: ["admin", "projektleiter"],
  },
};

// Sortierte Liste in offizieller UI-Reihenfolge (für Menüs):
export const PROJECT_PAGES_ORDER = [
  "overview",
  "daten",
  "tasks",
  "dailyReports",
  "controlling",
  "calculation",
  "documents",
  "plans",
  "planning",
  "settings",
];

/** Liefert das Page-Object für einen gegebenen URL-Pfad (z.B. location.pathname). */
export function getProjectPageFromPath(pathname) {
  for (const key of PROJECT_PAGES_ORDER) {
    const p = PROJECT_PAGES[key];
    if (p.absoluteRoute && pathname === p.absoluteRoute) return p;
    if (p.route && pathname.includes(`/${p.route}`)) return p;
  }
  return null;
}

/** Build the URL for a project page (relative to /app/projekte/:projectId/). */
export function buildProjectPageUrl(pageKey, projectId) {
  const p = PROJECT_PAGES[pageKey];
  if (!p) return null;
  if (p.absoluteRoute) return p.absoluteRoute;
  return `/app/projekte/${projectId}/${p.route}`;
}

/** Filter pages by user role and any custom predicate. */
export function getVisibleProjectPages(role) {
  const r = (role || "").toLowerCase();
  return PROJECT_PAGES_ORDER
    .map((key) => PROJECT_PAGES[key])
    .filter((p) => !p.roles || p.roles.includes(r));
}
